import React from 'react';
import {
    ImageAspectRatio,
    StyledCategory,
    StyledMediaWrapper,
    StyledTextBlock,
} from './styled';

import { TextBlockForContentCardsLayout } from '../TextBlockForContentCards';
import { ImageMedia, Link as LinkType, VideoMedia } from '~/lib/data-contract';
import { ImageProps, CategoryCardMedia } from '~/shared/components';
import { queriesIsolated, useThemeShade } from '~/theme';
import { useTheme } from '@emotion/react';

type Props = Partial<{
    image: ImageMedia;
    callToAction: LinkType;
    hidCallToActionButton: boolean;
    backgroundColor: string;
    headline: string;
    description: string;
    imagesVW: number;
    /**
     * Will stretch to outer container instead of using a aspect ratio
     */
    stretch: boolean;
    /**
     * Text can be placed "on" or "below the category card"
     */
    textPosition: TextBlockForContentCardsLayout;
    as?: React.ElementType;
    video?: VideoMedia;
    stretchTextContent?: boolean;
}> & { imageAspectRatio: ImageAspectRatio } & Pick<ImageProps, 'hoverStyle'>;

export const Category = (props: Props) => {
    const {
        imageAspectRatio,
        image,
        callToAction,
        backgroundColor: initialBackgroundColor,
        headline,
        description,
        imagesVW = 100,
        stretch = false,
        textPosition,
        video,
        stretchTextContent,
        hidCallToActionButton,
        ...rest
    } = props;
    const { traits } = useTheme();
    const moduleTheme = useThemeShade({ backgroundColor: initialBackgroundColor });
    const position = (textPosition ||
        traits.contentCard.textPosition) as TextBlockForContentCardsLayout;

    const hasTextContent = Boolean(headline || description);

    return (
        <StyledCategory
            href={callToAction?.url || ''}
            renderEmptyLinksAsDiv
            title={callToAction?.title}
            target={callToAction?.target}
            type="router"
            {...rest}
        >
            <StyledMediaWrapper {...imageAspectRatio} stretch={stretch} mediaOnly={!hasTextContent}>
                <CategoryCardMedia
                    image={image}
                    video={video}
                    imgSizes={`${queriesIsolated.xs} 100vw, ${queriesIsolated.sm} 100vw, ${imagesVW}vw`}
                    is4by5
                />
            </StyledMediaWrapper>
            {hasTextContent && (
                <StyledTextBlock
                    headlineVariant="display4"
                    stretchTextContent={stretchTextContent}
                    headline={headline}
                    moduleTheme={{
                        ...moduleTheme,
                        backgroundColor: initialBackgroundColor,
                    }}
                    text={description}
                    textPosition={position}
                    callToAction={hidCallToActionButton ? undefined : callToAction}
                />
            )}
        </StyledCategory>
    );
};
